import React, { useState } from 'react';
import { ActivityRow } from './ActivityRow';
import { ActivityItem } from './homeMeta';

interface Props {
  title: string;
  items: ActivityItem[];
  now: Date;
  /** Rows before "Show more". */
  pageSize: number;
  emptyMessage: string;
  loading?: boolean;
  /** Beside the title, e.g. a live marker. */
  note?: React.ReactNode;
  /** Pinned panels read as a warning. */
  tone?: 'default' | 'danger';
  /** Show comments' thread badges (internal, shared). */
  showThreads: boolean;
}

/**
 * A titled list of Home rows (#670) that grows a page at a time. New
 * rows arrive at the top, so growing keeps the reader's place.
 */
export const ActivityPanel: React.FC<Props> = ({
  title,
  items,
  now,
  pageSize,
  emptyMessage,
  loading = false,
  note,
  tone = 'default',
  showThreads,
}) => {
  const [shown, setShown] = useState(pageSize);
  const rest = items.length - shown;

  return (
    <section
      aria-label={title}
      className={
        tone === 'danger'
          ? 'flex min-w-0 flex-col overflow-hidden rounded-lg border border-danger/40 bg-raised'
          : 'flex min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-raised'
      }
    >
      <header className="flex items-center gap-2 border-b border-line/60 px-4 py-2.5">
        <h2 className={tone === 'danger' ? 'text-[13px] font-medium text-danger' : 'text-[13px] font-medium text-fg'}>
          {title}
        </h2>
        {!loading && <span className="text-[13px] text-fg-muted">{items.length}</span>}
        {note}
      </header>
      {loading ? (
        <p className="px-4 py-8 text-center text-[13px] text-fg-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="px-4 py-8 text-center text-[13px] text-fg-muted">{emptyMessage}</p>
      ) : (
        <ul className="divide-y divide-line/50">
          {items.slice(0, shown).map((item) => (
            <ActivityRow key={item.key} item={item} now={now} showThreads={showThreads} />
          ))}
        </ul>
      )}
      {!loading && rest > 0 && (
        <button
          type="button"
          onClick={() => setShown((n) => n + pageSize)}
          className="border-t border-line/60 px-4 py-2 text-left text-[12px] text-fg-muted hover:bg-fg/[0.03] hover:text-fg"
        >
          Show {Math.min(rest, pageSize)} more of {rest}
        </button>
      )}
    </section>
  );
};

export default ActivityPanel;
